import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import type { BranchSummaryItem, BrandAverageMetrics } from '../../hooks/useBrandQueries';

interface BranchSummaryCardProps {
  branch: BranchSummaryItem;
  brandAverage: BrandAverageMetrics;
  onClick: () => void;
  performanceRange?: 'high' | 'medium' | 'low';
}

interface MetricRowProps {
  label: string;
  value: number;
  currency?: string;
  labelClass?: string;
  valueClass?: string;
}

const MetricRow: React.FC<MetricRowProps> = ({
  label,
  value,
  currency,
  labelClass = 'text-sm text-text-secondary',
  valueClass = 'text-base font-semibold text-text-primary'
}) => {
  const formattedValue = currency
    ? `${value.toLocaleString('fa-IR')} ${currency}`
    : value.toLocaleString('fa-IR');
  
  return (
    <div className="flex justify-between items-center">
      <span className={labelClass}>{label}</span>
      <span className={valueClass}>{formattedValue}</span>
    </div>
  );
};

interface PerformanceIndicatorProps {
  isAboveAverage: boolean;
  className?: string;
}

const PerformanceIndicator: React.FC<PerformanceIndicatorProps> = ({
  isAboveAverage,
  className = ''
}) => {
  return (
    <div
      className={`flex items-center gap-1 ${className}`}
      aria-label={isAboveAverage ? 'عملکرد بالاتر از میانگین' : 'عملکرد پایین‌تر از میانگین'}
    >
      {isAboveAverage ? (
        <>
          <TrendingUp className="w-5 h-5" />
          <span className="text-sm font-medium">بالاتر از میانگین</span>
        </>
      ) : (
        <>
          <TrendingDown className="w-5 h-5" />
          <span className="text-sm font-medium">پایین‌تر از میانگین</span>
        </>
      )}
    </div>
  );
};

const BranchSummaryCard: React.FC<BranchSummaryCardProps> = ({
  branch,
  brandAverage,
  onClick,
  performanceRange
}) => {
  // Determine if branch is performing above average
  const isAboveAverage = branch.performanceScore > brandAverage.performanceScore;
  
  // Get range-based styling
  const getRangeStyling = () => {
    switch (performanceRange) {
      case 'high':
        return 'border-l-4 border-l-green-500 bg-green-50/50 dark:bg-green-900/10';
      case 'low':
        return 'border-l-4 border-l-red-500 bg-red-50/50 dark:bg-red-900/10';
      case 'medium':
        return 'border-l-4 border-l-yellow-500 bg-yellow-50/50 dark:bg-yellow-900/10';
      default:
        return '';
    }
  };
  
  const getRangeIcon = () => {
    switch (performanceRange) {
      case 'high':
        return '🟢';
      case 'low':
        return '🔴';
      case 'medium':
        return '🟡';
      default:
        return null;
    }
  };
  
  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };
  
  return (
    <div
      className={`card-base card-padding card-hover cursor-pointer transition-all duration-base min-h-[180px] touch-manipulation ${getRangeStyling()}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`مشاهده جزئیات شعبه ${branch.branchName}`}
    >
      {/* Header with branch name and performance indicator */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3 pb-3 border-b border-border-default">
        <div className="flex items-center gap-2">
          {getRangeIcon() && <span className="text-lg">{getRangeIcon()}</span>}
          <h3 className="title-card text-text-primary text-lg md:text-xl">{branch.branchName}</h3>
        </div>
        <PerformanceIndicator
          isAboveAverage={isAboveAverage}
          className={isAboveAverage ? 'text-status-success' : 'text-status-warning'}
        />
      </div>
      
      {/* Metrics */}
      <div className="space-y-2">
        <MetricRow
          label="فروش ماه جاری"
          value={branch.currentMonthRevenue}
          currency={branch.currency}
        />
        <MetricRow
          label="تعداد مشتریان"
          value={branch.customerCount}
        />
        <MetricRow
          label="میانگین تراکنش"
          value={branch.avgTransactionValue}
          currency={branch.currency}
        />
      </div>
      
      {/* Additional metrics (optional, shown on hover or always visible) */}
      <div className="mt-3 pt-3 border-t border-border-default space-y-1">
        <div className="flex justify-between items-center">
          <span className="text-xs text-text-tertiary">رضایت مشتری</span>
          <span className="text-sm font-medium text-text-secondary">
            {branch.satisfactionScore.toLocaleString('fa-IR')}%
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-text-tertiary">نرخ عضویت باشگاه</span>
          <span className="text-sm font-medium text-text-secondary">
            {branch.clubMembershipRate.toLocaleString('fa-IR')}%
          </span>
        </div>
      </div>
    </div>
  );
};

export default BranchSummaryCard;
